import axios from 'axios';
import { toApiError } from './errors';

/**
 * Single shared axios instance for all API calls.
 *
 * The API is served from the same origin as the app (the dev server proxies /api,
 * see setupProxy.js; nginx does it in production), so the browser sends the
 * HttpOnly auth cookies itself and sets Origin on every write. Nothing here reads
 * or sends a token.
 *
 * Sessions: the access cookie lasts 15 minutes and the client can't see its
 * expiry, so a 401 is the signal to refresh. See refreshSession() and the
 * response interceptor below.
 */
const apiClient = axios.create({ baseURL: '/api/v1' });

// Refreshing on a 401 from these would loop, or hide the real error (a wrong
// password is a 401 too).
const NO_REFRESH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

// Set by AuthProvider: what to do when the session is gone for good.
let authHandlers = {
  onSessionLost: () => {},
  onLoginRequired: () => {},
};
export const setAuthHandlers = (handlers) => {
  authHandlers = { ...authHandlers, ...handlers };
};

// A hint, not a secret: whether this browser had a session, so a logged-out
// visitor's first GET /me 401 doesn't also cost a pointless refresh call.
const SESSION_HINT = 'cp-session';
export const markSession = (present) => {
  try {
    if (present) localStorage.setItem(SESSION_HINT, '1');
    else localStorage.removeItem(SESSION_HINT);
  } catch (e) {
    // Storage blocked: every 401 then just tries a refresh
  }
};
const hadSession = () => {
  try {
    return localStorage.getItem(SESSION_HINT) === '1';
  } catch (e) {
    return true;
  }
};

// Cookies are shared by every tab, and each refresh token works once: if two
// tabs refreshed with the same token, the loser's 401 would clear the cookies the
// winner just got. So the refresh runs under a cross-tab lock, and inside it we
// first check whether another tab already refreshed.
const runExclusive = (task) =>
  typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request
    ? navigator.locks.request('cp-auth-refresh', task)
    : task();

const refreshUnderLock = () =>
  runExclusive(async () => {
    try {
      await apiClient.get('/me', { _noRefresh: true });
      return true;
    } catch (e) {
      // Still 401: this tab has to refresh
    }
    try {
      await apiClient.post('/auth/refresh');
      markSession(true);
      return true;
    } catch (e) {
      return false;
    }
  });

// Requests that hit a 401 at the same time in one tab share one refresh
let pendingRefresh = null;
export const refreshSession = () => {
  if (!pendingRefresh) {
    pendingRefresh = refreshUnderLock().finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
};

const isRead = (config) => (config.method || 'get').toLowerCase() === 'get';

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const skip =
      !config ||
      !response ||
      response.status !== 401 ||
      config._retried ||
      config._noRefresh ||
      NO_REFRESH_PATHS.some((path) => (config.url || '').startsWith(path)) ||
      (config._sessionProbe && !hadSession());
    if (skip) {
      return Promise.reject(toApiError(error));
    }

    config._retried = true;
    if (await refreshSession()) {
      return apiClient(config);
    }

    // The session is over (idle for a day, past 7 days, or logged out elsewhere);
    // the backend has already cleared the cookies.
    markSession(false);
    authHandlers.onSessionLost();
    if (config._sessionProbe) {
      return Promise.reject(toApiError(error));
    }
    if (isRead(config)) {
      // Public pages just show the logged-out view
      return apiClient(config);
    }
    authHandlers.onLoginRequired();
    return Promise.reject(toApiError(error));
  }
);

export default apiClient;
